// DestinationsPage.jsx — aligned with Figma Home-Fixed design
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./../PaymentPage.css";
import "./DestinationsPage.css";
import BrandHeader from "../header/BrandHeader";
import SearchBar from "../search-bar/SearchBar";
import NetworkCarousel from "./NetworkCarousel";
import { WA_CS_DISPLAY, WA_CS_LINK } from "../config/whatsapp";
import { useLanguage } from "../i18n/LanguageContext";
import RichText from "../i18n/RichText";

const API_URL = "https://api.artamobile.id/v1/region";

function formatRupiah(v) {
    if (v == null) return "—";
    let amount = v;
    if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (/^\d+(?:\.\d+)?k$/.test(s)) amount = parseFloat(s) * 1000;
        else if (/^\d+(?:\.\d+)?$/.test(s)) amount = parseFloat(s);
    }
    try {
        return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(amount);
    } catch {
        return `Rp ${amount}`;
    }
}

const ICON = (name, size = 14) => (
    <img src={`${process.env.PUBLIC_URL}/icons/${name}.svg`} width={size} height={size} alt="" aria-hidden />
);

// Labels live in the dictionaries under home.tabs.<key>.
const TABS = [
    { key: "popular", icon: "ok-hand" },
    { key: "umrah",   icon: "moon" },
    { key: "multi",   icon: "earth-airplane" },
];

const FLAG_OVERRIDES = {
    hajj2: "sa",
};

const getFlagUrl = (code) => {
    if (!code) return null;
    const mapped = FLAG_OVERRIDES[code.toLowerCase()] || (code.length === 2 ? code.toLowerCase() : null);
    return mapped ? `https://flagcdn.com/w40/${mapped}.png` : null;
};

const isTabKey = (v) => TABS.some((t) => t.key === v);

// Step copy and the FAQ live in the dictionaries (home.steps, home.faqs); only
// the icons are fixed here, matched to the steps by position.
const ESIM_STEP_ICONS = ["shopping-bag", "qr-code", "check"];

const DestinationsPage = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const { t } = useLanguage();
    const [destinations, setDestinations] = useState([]);
    const [activeTab, setActiveTab] = useState(() => {
        const initial = new URLSearchParams(window.location.search).get("tab");
        return isTabKey(initial) ? initial : "popular";
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [expandedFaq, setExpandedFaq] = useState(0);
    const [promoAtStart, setPromoAtStart] = useState(true);
    const [promoAtEnd, setPromoAtEnd] = useState(false);
    const carouselRef = useRef(null);
    const abortRef = useRef(null);

    const scrollPromo = (dir) => {
        const el = carouselRef.current;
        if (!el) return;
        const cardW = (el.offsetWidth - 20) / 2 + 20;
        el.scrollBy({ left: dir * cardW, behavior: 'smooth' });
    };

    const handlePromoScroll = () => {
        const el = carouselRef.current;
        if (!el) return;
        setPromoAtStart(el.scrollLeft <= 4);
        setPromoAtEnd(el.scrollLeft + el.offsetWidth >= el.scrollWidth - 4);
    };

    useEffect(() => {
        const sp = new URLSearchParams(search);
        if (sp.get("tab") !== activeTab) {
            sp.set("tab", activeTab);
            navigate({ search: `?${sp.toString()}` }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const loadRegions = useCallback(async () => {
        setLoading(true);
        setError(null);
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        try {
            const res = await fetch(API_URL, { signal: ctrl.signal });
            if (!res.ok) throw new Error(`Failed to fetch destinations (HTTP ${res.status})`);
            const data = await res.json();
            let list = Array.isArray(data) ? data : [];

            const partner = new URLSearchParams(search).get("partner");
            if (partner === "381jepangtrip") {
                list = list.filter((item) => item.regionCode?.toLowerCase() !== "jp");
            } else {
                list = list.filter((item) => item.regionCode?.toLowerCase() !== "381jp");
            }

            setDestinations(list);
        } catch (err) {
            if (err.name !== "AbortError") setError(err);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        loadRegions();
        return () => abortRef.current?.abort();
    }, [loadRegions]);

    const handleSelectRegion = (regionCode) => {
        navigate(`/region/${regionCode}${search || ""}`);
    };

    const filtered = useMemo(() => {
        const list = destinations.filter((r) => {
            if (activeTab === "popular") return r.popular !== undefined;
            if (activeTab === "umrah")   return r.umrah !== undefined;
            if (activeTab === "multi")   return r.multi !== undefined;
            return true;
        });

        return list.slice().sort((a, b) => {
            if (activeTab === "popular") {
                const ap = a.popular ?? Number.POSITIVE_INFINITY;
                const bp = b.popular ?? Number.POSITIVE_INFINITY;
                if (ap !== bp) return ap - bp;
            }
            if (activeTab === "umrah") {
                const au = a.umrah ?? Number.POSITIVE_INFINITY;
                const bu = b.umrah ?? Number.POSITIVE_INFINITY;
                if (au !== bu) return au - bu;
            }
            if (activeTab === "multi") {
                const am = a.bundleOrder ?? Number.POSITIVE_INFINITY;
                const bm = b.bundleOrder ?? Number.POSITIVE_INFINITY;
                if (am !== bm) return am - bm;
            }
            return (a.name || "").localeCompare(b.name || "");
        });
    }, [destinations, activeTab]);

    const promoItems = useMemo(() => {
        return destinations
            .filter((r) => typeof r.promo === "number" && r.promo >= 1)
            .sort((a, b) => a.promo - b.promo);
    }, [destinations]);

    const renderCard = (region, isPromo = false) => (
        <button
            key={region.regionCode}
            className="destination-card"
            onClick={() => handleSelectRegion(region.regionCode)}
            aria-label={t("home.openRegion", { name: region.name })}
        >
            <div className="card-image-wrap">
                <img
                    className="destination-image"
                    src={region.shopImageUrl}
                    alt={region.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = "/fallback-country.png"; }}
                />
                {isPromo && (
                    <div className="card-badges">
                        {region.popular !== undefined && (
                            <span className="badge badge-popular">{ICON("like", 10)} {t("common.popular")}</span>
                        )}
                        <span className="badge badge-promo">{ICON("discount-fire", 10)} {t("common.promo")}</span>
                    </div>
                )}
            </div>
            <div className="destination-details">
                <div className="card-name-row">
                    {getFlagUrl(region.regionCode) && (
                        <img
                            className="card-flag"
                            src={getFlagUrl(region.regionCode)}
                            alt=""
                            aria-hidden
                            loading="lazy"
                        />
                    )}
                    <h2 className="destination-name">{region.name}</h2>
                </div>
                <div className="card-price-row">
                    <div className="card-price-info">
                        <span className="price-label">{t("home.startingFrom")}</span>
                        <span className="price-value">{formatRupiah(region.startingPrice)}</span>
                    </div>
                    <span className="card-arrow-btn" aria-hidden>›</span>
                </div>
            </div>
        </button>
    );

    return (
        <div className="destinations-container">
            {/* Hero — contains logo, background illustration, title, search */}
            <div
                className="destinations-hero"
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-illustration.png)` }}
            >
                <div className="destinations-topbar">
                    <BrandHeader />
                </div>
                <h1 className="destinations-title">
                    <RichText text={t("home.title")} highlightClassName="title-highlight" />
                </h1>
                <p className="cta-text">{t("home.cta")}</p>
                <div className="destinations-search">
                    <SearchBar destinations={destinations} />
                </div>
                <div className="mt-10">
                    <NetworkCarousel />
                </div>
            </div>

            {/* Lagi Promo! */}
            {!loading && promoItems.length > 0 && (
                <section className="section-block promo-block">
                    <div className="section-header-row">
                        <h2 className="section-title">{ICON("flame", 20)} {t("home.promoTitle")}</h2>
                        {promoItems.length > 2 && (
                            <div className="promo-nav">
                                <button
                                    className="promo-nav-btn"
                                    aria-label={t("home.prev")}
                                    disabled={promoAtStart}
                                    onClick={() => scrollPromo(-1)}
                                >
                                    {ICON("arrow-left", 14)}
                                </button>
                                <button
                                    className="promo-nav-btn"
                                    aria-label={t("home.next")}
                                    disabled={promoAtEnd}
                                    onClick={() => scrollPromo(1)}
                                >
                                    {ICON("arrow-right", 14)}
                                </button>
                            </div>
                        )}
                    </div>
                    <div
                        className="promo-carousel-wrap"
                        ref={carouselRef}
                        onScroll={handlePromoScroll}
                    >
                        <div className="promo-cards">
                            {promoItems.map((r) => renderCard(r, true))}
                        </div>
                    </div>
                </section>
            )}

            {/* Pilihan Paket Lainnya */}
            <section className="section-block">
                {!loading && (
                    <div className="section-header-row">
                        <h2 className="section-title">{t("home.otherPackages")}</h2>
                    </div>
                )}

                <div className="sticky-tabs" role="tablist" aria-label={t("home.categoryAria")}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`country-button ${activeTab === tab.key ? "active" : ""}`}
                            onClick={() => { setActiveTab(tab.key); setShowAll(false); }}
                        >
                            {ICON(tab.icon, 14)} {t(`home.tabs.${tab.key}`)}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="cards-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div className="destination-card skeleton" key={i}>
                                <div className="skeleton-img" />
                                <div className="skeleton-lines">
                                    <div className="line w60" />
                                    <div className="line w40" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && error && (
                    <div className="destinations-error">
                        <p>{t("home.loadError")} {error.message}</p>
                        <button className="retry-btn" onClick={loadRegions}>{t("home.retry")}</button>
                    </div>
                )}

                {!loading && !error && (
                    filtered.length === 0
                        ? <div className="empty-state">{t("home.empty")}</div>
                        : <>
                            <div className="cards-grid">{(showAll ? filtered : filtered.slice(0, 6)).map((r) => renderCard(r, false))}</div>
                            {filtered.length > 6 && (
                                <button className="see-all-btn" onClick={() => setShowAll((v) => !v)}>
                                    {showAll ? t("home.showLess") : t("common.seeAll")}
                                </button>
                            )}
                          </>
                )}
            </section>

            {/* No Ribet! Cara Pakai eSIM */}
            <section className="section-block esim-block">
                <div className="esim-green-bg" />
                <div className="esim-section-header">
                    {ICON("happy-face", 24)}
                    <h2 className="section-title">{t("home.howTitle")}</h2>
                </div>
                <div className="esim-steps-row">
                    {t("home.steps").map((step, i) => (
                        <div className="esim-step-card" key={i}>
                            {ICON(ESIM_STEP_ICONS[i], 24)}
                            <div className="esim-step-body">
                                <span className="esim-step-num">{i + 1}</span>
                                <p className="esim-step-title">{step.title}</p>
                                <p className="esim-step-desc">{step.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tanya Jawab */}
            <section className="section-block">
                <div className="section-header-row">
                    <h2 className="section-title">{ICON("help-chat", 24)} {t("home.faqTitle")}</h2>
                </div>
                <div className="faq-list">
                    {t("home.faqs").map((faq, i) => (
                        <div className="faq-item" key={i}>
                            <button
                                className="faq-question"
                                onClick={() => setExpandedFaq(expandedFaq === i ? -1 : i)}
                                aria-expanded={expandedFaq === i}
                            >
                                <span className="faq-q-text">{faq.q}</span>
                                <span className={`faq-chevron ${expandedFaq === i ? "open" : ""}`}>{ICON("chevron-down", 16)}</span>
                            </button>
                            {expandedFaq === i && faq.a && (
                                <div className="faq-answer">{faq.a}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Ada Kendala */}
            <div className="ada-kendala-card">
                <div className="ada-kendala-content">
                    <div className="ada-kendala-texts">
                        <p className="ada-kendala-title">{t("help.title")}</p>
                        <p className="ada-kendala-subtitle">{t("help.desc")}</p>
                    </div>
                    <a
                        className="ada-kendala-btn"
                        href={WA_CS_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                    >{t("help.cta")}</a>
                </div>
                <img
                    className="ada-kendala-illustration"
                    src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                    alt=""
                    aria-hidden
                />
            </div>

            {/* Footer */}
            <footer className="page-footer">
                <div className="footer-main">
                    <div className="footer-top">
                        <img
                            className="footer-logo"
                            src="https://d1qsbuaxtzmcn4.cloudfront.net/brand/arta-logo.png"
                            alt="Arta Mobile"
                        />
                        <p className="footer-desc">
                            {t("home.footerDesc")}
                        </p>
                    </div>
                    <div className="footer-follow-row">
                        <span className="footer-follow-label">{t("home.followUs")}</span>
                        <div className="footer-social-icons">
                            <a href="https://www.facebook.com/p/Arta-Mobile-61575318069233/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                {ICON("facebook", 20)}
                            </a>
                            <a href="https://www.instagram.com/artamobile.id/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                {ICON("instagram", 20)}
                            </a>
                            <a href="https://www.tiktok.com/@artamobile.id" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                {ICON("tiktok", 20)}
                            </a>
                        </div>
                    </div>
                    <div className="footer-contact">
                        <div className="footer-contact-row">
                            {ICON("location", 14)}
                            <span className="footer-contact-text">Menara Agrinas Palma Lt 15 Unit 3, Jl. H.R. Rasuna Said Kav. VI No. 9, Jakarta, Indonesia</span>
                        </div>
                        <div className="footer-contact-row">
                            {ICON("mail", 14)}
                            <span className="footer-contact-text">contact@artamobile.id</span>
                        </div>
                        <div className="footer-contact-row">
                            {ICON("phone", 14)}
                            <a
                                className="footer-contact-text footer-contact-link"
                                href={WA_CS_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {WA_CS_DISPLAY}
                            </a>
                        </div>
                    </div>
                </div>
                <p className="footer-copyright">Copyright © 2026 PT. Arta Komunikasi Global. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default DestinationsPage;
